// src/routes/AdminRoutes.js
import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from '../pages/admin/AdminLayout';

// Lazy-loaded components
const StudentDashboard = lazy(() => import('../dashboards/StudentDashboard'));
const MentorDashboard = lazy(() => import('../dashboards/MentorDashboard'));
const VendorDashboard = lazy(() => import('../dashboards/VendorDashboard'));
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const AllUsers = lazy(() => import('../pages/admin/AllUsers'));
const CategoriesManage = lazy(() => import('../pages/admin/Categories/CategoriesManage'));
const ViewRoles = lazy(() => import('../pages/admin/Settings/ViewRoles'));
const SystemUsers = lazy(() => import('../pages/admin/Settings/SystemUsers'));
const ViewSystemUser = lazy(() => import('../pages/admin/Settings/ViewSystemUser'));
const EditSystemUser = lazy(() => import('../pages/admin/Settings/EditSystemUser'));
const SettingsGeneral = lazy(() => import('../pages/admin/Settings/SettingsGeneral'));
const Tags = lazy(() => import('../pages/admin/Posts/Tags'));
const Posts = lazy(() => import('../pages/admin/Posts/Posts'));
const PostViews = lazy(() => import('../pages/admin/Posts/PostViews'));
const CoursesManage = lazy(() => import('../pages/admin/Courses/CoursesManage'));
const CourseCreate = lazy(() => import('../pages/admin/Courses/CourseCreate'));
const CourseEdit = lazy(() => import('../pages/admin/Courses/CourseEdit'));
const ChallengesManage = lazy(() => import('../pages/admin/Challenges/ChallengesManage'));
const ProjectsManage = lazy(() => import('../pages/admin/Projects/ProjectsManage'));
const ProductCategoriesManage = lazy(() => import('../pages/admin/Products/ProductCategoriesManage'));
const TransactionsManage = lazy(() => import('../pages/admin/Transactions/TransactionsManage'));
const CertificatesManage = lazy(() => import('../pages/admin/Certificates/CertificatesManage'));
const SystemAlertsManage = lazy(() => import('../pages/admin/SystemAlerts/SystemAlertsManage'));

const ProtectedRoute = ({ role, allowedRoles, children }) => {
  if (!role || !allowedRoles.includes(role)) {
    return <Navigate to="/signin" replace />;
  }
  return children;
};

const AdminRoutes = ({ userRole, onLogout }) => {
  return (
    <AdminLayout onLogout={onLogout}>
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          {/* Role-Specific Dashboards */}
          <Route
            path="student/dashboard"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['STUDENT']}>
                <StudentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="mentor/dashboard"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['MENTOR']}>
                <MentorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="vendor/dashboard"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['VENDOR']}>
                <VendorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="dashboard"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <AdminDashboard onLogout={onLogout} />
              </ProtectedRoute>
            }
          />

          {/* Admin Management Routes */}
          <Route
            path="users-manage"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <AllUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="vendors-manage"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <SystemUsers /> {/* Adjust if you have a VendorsManage */}
              </ProtectedRoute>
            }
          />
          <Route
            path="courses-manage"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <CoursesManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="courses/create"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <CourseCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="courses/edit/:id"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <CourseEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="challenges-manage"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <ChallengesManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="projects-manage"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <ProjectsManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="categories-manage"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <CategoriesManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="product_categories"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <ProductCategoriesManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="transactions-manage"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <TransactionsManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="certificates-manage"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <CertificatesManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="system-alerts-manage"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <SystemAlertsManage />
              </ProtectedRoute>
            }
          />

          {/* Additional Admin Routes */}
          <Route
            path="roles"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <ViewRoles />
              </ProtectedRoute>
            }
          />
          <Route
            path="system-users"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <SystemUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="users/view/:id"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <ViewSystemUser />
              </ProtectedRoute>
            }
          />
          <Route
            path="users/edit/:id"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <EditSystemUser />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings-general"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <SettingsGeneral />
              </ProtectedRoute>
            }
          />
          <Route
            path="tags"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <Tags />
              </ProtectedRoute>
            }
          />
          <Route
            path="posts"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <Posts />
              </ProtectedRoute>
            }
          />
          <Route
            path="post-views"
            element={
              <ProtectedRoute role={userRole} allowedRoles={['ADMIN']}>
                <PostViews />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </AdminLayout>
  );
};

export default AdminRoutes;